import { PlayerEnvironment } from './player_environment';
import MouseInteraction from './mouse_interaction';
import PlayerDoubleTapSeek from './player_double_tap_seek';
import isFeatureToggleOn from './utils/feature_toggle';
import { PlayerTokenRefresh } from './player_token_refresh';
import PlayerContinueWatching from './player_continue_watching';
import UserIdWatermark from './components/user_id_watermark';
import ContentTitle from './components/content_title';
import PlayerKeyboardPress from './player_keyboard_press';
import Adult from './plugins/adult';
import VideoInformation from "./components/video_information"
import MobileBitrate from './mobile_bitrate';
import Hotfix from './hotfix/hotfix';
import nodelistToArray from './utils/nodelist_to_array';
import PlayerStartAtTime from './player_start_at_time';
import PlayerRetry from './player_retry';
import PlayerContentPreview from './player_content_preview';
import PlayerContentGatingLogin from './player_content_gating_login';
import MessageManager from './message_manager';
import KMKLabs from './adapters/kmklabs_adapter';
import VolumeStateService from './service/volume_state_service';
import EpisodeChooser from './components/episode_chooser';
import PlayerDvr from './player_dvr';
import HdcpBlocker from './hdcp_blocker';
import DrmBlocker from './drm_blocker';
import FluidHeight from './fluid_height';

export default class PlayerAfterSetup {
  constructor({ tech, data, ads, autoplay, error } = {}) {
    this._tech = tech;
    this._data = data;
    this._ads = ads;
    this._autoplay = autoplay;
    this._error = error;
    this._mouseInteraction = new MouseInteraction({ tech });
    this._playerDoubleTapSeek = new PlayerDoubleTapSeek(tech);
    this._playerTokenRefresh = new PlayerTokenRefresh({ tech, data });
    this._playerContinueWatching = new PlayerContinueWatching(data, tech);
    this._contentTitle = new ContentTitle({ tech, data });
    this._playerKeyboardPress = new PlayerKeyboardPress(tech, this.data().playUUID(), data);
    this._videoInformation = new VideoInformation({ tech, data });
    this._mobileBitrate = new MobileBitrate({ tech, data });
    this._hotfix = new Hotfix( tech, data );
    this._playerStartAtTime = new PlayerStartAtTime({ tech });
    this._playerRetry = new PlayerRetry({ tech, data });
    this._playerContentPreview = new PlayerContentPreview({ tech, data });
    this._playerContentGatingLogin = new PlayerContentGatingLogin({ tech, data });
    this._messageManager = new MessageManager({ tech, data });
    this._volumeStateService = new VolumeStateService({ tech, data });
    this._episodeChooser = new EpisodeChooser({ tech, data });
    this._playerDvr = new PlayerDvr({ tech, data });
    this._hdcpBlocker = new HdcpBlocker({ tech, data });
    this._drmBlocker = new DrmBlocker({ tech });
    this._fluidHeight = new FluidHeight({ tech, ads, data });
  }

  tech() {
    return this._tech;
  }

  data(){
    return this._data;
  }

  ads(){
    return this._ads;
  }

  autoplay(){
    return this._autoplay;
  }

  error() {
    return this._error;
  }

  mouseInteraction() {
    return this._mouseInteraction;
  }

  playerDoubleTapSeek() {
    return this._playerDoubleTapSeek;
  }

  playerTokenRefresh() {
    return this._playerTokenRefresh;
  }

  playerContinueWatching() {
    return this._playerContinueWatching;
  }

  userIdWatermark() {
    return this._userIdWatermark;
  }

  contentTitle() {
    return this._contentTitle;
  }

  playerKeyboardPress() {
    return this._playerKeyboardPress;
  }

  videoInformation() {
    return this._videoInformation;
  }

  mobileBitrate() {
    return this._mobileBitrate;
  }

  hotfix() {
    return this._hotfix;
  }

  playerStartAtTime() {
    return this._playerStartAtTime;
  }

  playerRetry() {
    return this._playerRetry;
  }

  playerContentPreview() {
    return this._playerContentPreview;
  }

  playerContentGatingLogin() {
    return this._playerContentGatingLogin;
  }

  messageManager() {
    return this._messageManager;
  }

  volumeStateService() {
    return this._volumeStateService;
  }

  episodeChooser() {
    return this._episodeChooser;
  }

  playerDvr() {
    return this._playerDvr;
  }

  hdcpBlocker() {
    return this._hdcpBlocker;
  }

  drmBlocker() {
    return this._drmBlocker;
  }

  fluidHeight() {
    return this._fluidHeight;
  }

  init() {
    this.showPlayer();
    this.addMobileWebClass();

    this.bindLoadstart();
    this.bindPlayerRetry();
    this.bindError();

    this.initPlayerTokenRefresh();
    this.initUserIdWatermark();
    this.initContentTitle();
    this.initKeyboardPress();
    this.initAdultBlocker();
    this.initDoubleTapSeek();
    this.initMouseInteraction();
    this.initVideoInformation();
    this.initMobileBitrate();
    this.initHotfix();
    this.initContentPreview();
    this.initContentGatingLogin();
    this.initMessageManager();
    this.initVolumeStateService();
    this.initDvr();
    this.initHdcpBlocker();
    this.initDrmBlocker();
    this.initFluidHeight();

    this.startContentAtSpecificTime();

    this.appendBigPauseButton();
    this.appendUnmuteButton();
    this.appendBigSeekButton();
    this.appendShareButton();
    this.appendTopBar();
    this.appendEpisodeChooser();
    this.appendVideoEndedPanel();
    this.appendSkipIntroButton();
  }

  showPlayer() {
    this.tech().el().setAttribute('style', 'opacity: 1');
    this.tech().el().querySelector('video').setAttribute('style', 'opacity: 1');
  }

  addMobileWebClass() {
    if (PlayerEnvironment.isMobileSite() || this.data().testMobileSite()) {
      this.tech().el().addClass('vjs-mobile-web');
    }
  }

  initFluidHeight() {
    if (this.fluidHeight().shouldInit()) {
      this.fluidHeight().init();
    }
  }

  bindLoadstart() {
    if (this.data().drmEnabled() && this.isSafariOnMacSierra()) {
      this.tech().on('loadstart', () => {
        if (this.tech().error().code) {
          this.tech().error(this.tech().error().code)
        } else {
          this.removeExistingErrorBlocker();
        }
      });
    }
  }

  isSafariOnMacSierra() {
    return (
      PlayerEnvironment.browserName() === 'Safari' &&
      PlayerEnvironment.osName().includes('Mac Os') &&
      PlayerEnvironment.osVersion().startsWith('10')
    );
  }

  removeExistingErrorBlocker() {
    const errorBlocker = document.querySelectorAll('.vjs-generic-error, .vjs-player-error__section, .vjs-error-display');
    nodelistToArray(errorBlocker).forEach((node) => {
      node.parentNode.removeChild(node);
    });
  }

  bindPlayerRetry() {
    this.playerRetry().init();
  }

  bindError() {
    this.tech().on('error', () => {
      if (
        this.tech().errorCode() == 5 &&
        PlayerEnvironment.browserVersionMajor() == 12 &&
        PlayerEnvironment.isBrowserSafari()
      ) {
        this.removeExistingErrorBlocker();
      } else {
        if(!PlayerRetry.isErrorIntercepted(this.tech().errorCode())) {
          this.error().showError(this.tech().errorCode(), this.tech().errorMessage());
        }
      }
    });
  }

  initPlayerTokenRefresh() {
    if (this.data().tokenDuration() > 0) {
      this.playerTokenRefresh().init();
    }
  }

  initContinueWatching() {
    if (this.data().isVideo() && this.data().embed() == 'false') {
      this.playerContinueWatching().init();
    }
  }

  initUserIdWatermark() {
    if (this.data().isLivestreaming()) {
      this._userIdWatermark = new UserIdWatermark({
        tech: this.tech(),
        videoId: this.data().videoId(),
        rawWatermarkData: KMKLabs.watermark_timestamp()
      });
      this.userIdWatermark().init();
    }
  }

  initContentTitle() {
    if (PlayerEnvironment.isDesktopSite() && this.data().embed() !== 'true') {
      this.contentTitle().init();
    }
  }

  initKeyboardPress() {
    if (PlayerEnvironment.isDesktopSite()) {
      this.playerKeyboardPress().init();
    }
  }

  initAdultBlocker() {
    Adult.runPlugin({
      enable: this.data().adult(),
      testMode: this.data().testMode(),
      player: this.tech().plugin().instance()
    });
  }

  initDoubleTapSeek() {
    if ((PlayerEnvironment.isMobileSite() || this.data().testMobileSite()) && this.data().isVideo()) {
      this.playerDoubleTapSeek().init();
    }
  }

  initMouseInteraction() {
    if (PlayerEnvironment.isDesktopSite()) {
      this.mouseInteraction().init();
    }
  }

  initVideoInformation() {
    if (VideoInformation.shouldInit(this.data())) {
      this.videoInformation().init();
    }
  }

  initMobileBitrate() {
    if (
      PlayerEnvironment.isMobileSite() &&
      this.data().embed() === 'false' ||
      this.data().testMobileSite()
    ) {
      this.mobileBitrate().init();
    }
  }

  initHotfix() {
    this.hotfix().init();
  }

  initContentPreview() {
    if(this.data().preview()) this.playerContentPreview().init();
  }

  initContentGatingLogin() {
    const self = this

    if(window.isUserSignedIn == undefined){
      setTimeout(() => {
        self.initContentGatingLogin()
      }, 50)
    }else{
      self.renderContentGatingLogin()
    }
  }

  renderContentGatingLogin() {
    if(this.data().contentGatingLogin() && !this.data().preview()) this.playerContentGatingLogin().init();
  }

  initMessageManager() {
    if (this.messageManager().shouldInit()) {
      this.messageManager().init();
    }
  }

  initVolumeStateService() {
    if (this.volumeStateService().shouldInit()) {
      this.volumeStateService().init();
    }
  }

  initDvr() {
    if (PlayerDvr.shouldInit({ data: this.data() })) {
      this.playerDvr().init();
    }
  }

  initHdcpBlocker() {
    if (this.data().hdcpEnabled()) {
      this.hdcpBlocker().init();
    }
  }

  initDrmBlocker() {
    if(this.data().drmEnabled()) {
      this.drmBlocker().init();
    }
  }

  startContentAtSpecificTime() {
    const time = PlayerStartAtTime.startTime();

    if (time > 0) {
      this.playerStartAtTime().init(time);
    } else {
      this.initContinueWatching();
    }
  }

  appendBigPauseButton() {
    this.tech().addChild('VpBigPauseButton');
  }

  appendUnmuteButton() {
    if (this.data().mute() && this.data().embed() != 'true' && (PlayerEnvironment.isMobileSite() || this.data().testMobileSite())) {
      this.tech().addChild('VpUnmuteButton', {
        data: this.data(),
        ads: this.ads(),
        autoplay: this.autoplay(),
        tech: this.tech()
      });
    }
  }

  appendUnmuteOverlay() {
    if (this.data().mute() && (PlayerEnvironment.isMobileSite() || this.data().testMobileSite())) {
      this.tech().addChild('VpUnmuteOverlay', {
        data: this.data(),
        ads: this.ads(),
        autoplay: this.autoplay(),
        tech: this.tech(),
      });
    }
  }

  appendBigSeekButton() {
    if (this.data().isVideo()) {
      this.tech().addChild('VpBigSeekBackwardButton');
      this.tech().addChild('VpBigSeekForwardButton');
    }
  }

  appendShareButton() {
    if (this.data().shareButtonEnabled()) {
      this.tech().addChild('VpShareButton', {
        isPremier: this.data().isPremier(),
        videoId: this.data().videoId()
      });
    }
  }

  appendTopBar(){
    if (this.data().embed() === 'true'){
      this.tech().addChild('VpTopBar', {
        title: this.data().videoTitle(),
        userAvatarUrl: this.data().userAvatarUrl(),
        watchpageUrl: this.data().watchpageUrl(),
        userProfileUrl: this.data().userProfileUrl()
      });
    }
  }

  appendVideoEndedPanel(){
    if (this.data().isVideo()
      && this.data().preview() === false
      && this.data().embed() === 'false'
      && PlayerEnvironment.isWatchPage()
    ) {
      const shouldUseChapterCredit = isFeatureToggleOn('enable_chapters_credit_time') && this.data().chaptersCreditStart() > 0
      const endCreditTime = (shouldUseChapterCredit)
        ? this.data().chaptersCreditStart()
        : this.data().endCreditTime();
      this.tech().addChild('VpVideoEndedPanel', {
        videoId: this.data().videoId(),
        endCreditTime,
      });
    }
  }

  appendEpisodeChooser() {
    if (this.episodeChooser().shouldInit()) {
      this.episodeChooser().init();
    }
  }

  appendSkipIntroButton () {
    if (this.data().chaptersIntroStart() > 0 &&
      this.data().chaptersIntroEnd() > 0 &&
      this.data().chaptersIntroStart() < this.data().videoDuration() &&
      this.data().embed() === 'false' &&
      PlayerEnvironment.isWatchPage() &&
      this.data().isVideo() &&
      this.data().preview() === false
    ) {
      this.tech().addChild('VpSkipIntroButton', {
        chaptersIntroStart: this.data().chaptersIntroStart(),
        chaptersIntroEnd: this.data().chaptersIntroEnd(),
        playUUID: this.data().playUUID(),
        videoId: this.data().videoId(),
      });
    }
  }
}
